import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { 
  Anchor, 
  Plus, 
  Search, 
  Edit, 
  Copy, 
  Eye, 
  Mail, 
  MessageSquare, 
  Smartphone,
  BarChart3,
  Settings,
  Clock,
  DollarSign,
  Users
} from 'lucide-react';

const YachtCommunications = () => {
  const [activeLanguage, setActiveLanguage] = useState('FR');
  const [activeView, setActiveView] = useState('templates');

  // Sample communication templates data
  const communicationTemplates = [
    {
      id: 1,
      name: 'MYBA Charter Agreement & Terms',
      channels: ['email'],
      languages: ['FR', 'EN', 'RU', 'AR', 'ZH'],
      description: 'Contrat de charter MYBA avec conditions générales, APA breakdown et assurances requises',
      triggers: [
        { type: 'condition', text: 'Option confirmée' },
        { type: 'payment', text: 'Deposit 50% ready' },
        { type: 'yacht', text: 'Central agent approved' }
      ],
      stats: {
        sent: 847,
        signatureRate: '92.3%',
        avgTime: '2.4 jours',
        charterValue: '€127M'
      },
      active: true
    },
    {
      id: 2,
      name: 'Guest Preference & Provisioning Form',
      channels: ['email', 'whatsapp'],
      languages: ['FR', 'EN', 'RU', 'AR'],
      description: 'Formulaire détaillé : dietary requirements, beverage preferences, activities, medical needs, children amenities',
      triggers: [
        { type: 'time', text: '60 jours avant' },
        { type: 'payment', text: 'Deposit received' },
        { type: 'crew', text: 'Captain assigned' }
      ],
      stats: {
        sent: 623,
        completionRate: '87.4%',
        crewSatisfaction: '4.8/5',
        avgProvisioningCost: '€45K'
      },
      active: true
    },
    {
      id: 3,
      name: 'Welcome & Embarkation Instructions',
      channels: ['email', 'whatsapp', 'sms'],
      languages: ['FR', 'EN', 'RU', 'AR', 'ZH'],
      description: 'Informations embarquement : marina details, helicopter transfers, customs procedures, crew presentation',
      triggers: [
        { type: 'time', text: '48h avant' },
        { type: 'yacht', text: 'Yacht positioned' },
        { type: 'crew', text: 'All crew onboard' }
      ],
      stats: {
        sent: 892,
        readRate: '96.7%',
        onTimeEmbark: '94.2%',
        guestSatisfaction: '4.9/5'
      },
      active: true
    },
    {
      id: 4,
      name: 'Daily Charter Updates',
      channels: ['whatsapp', 'sms'],
      languages: ['FR', 'EN', 'RU'],
      description: 'Mises à jour quotidiennes : weather forecast, suggested itinerary, provisioning status, special requests',
      triggers: [
        { type: 'time', text: 'Daily 08:00' },
        { type: 'condition', text: 'Charter active' },
        { type: 'yacht', text: 'Position update' }
      ],
      stats: {
        sent: 2456,
        responseRate: '78.3%',
        itineraryChanges: '23%',
        avgDailySpend: '€12K'
      },
      active: false
    }
  ];

  const getChannelIcon = (channel) => {
    switch(channel) {
      case 'email': return <Mail className="w-4 h-4" />;
      case 'whatsapp': return <MessageSquare className="w-4 h-4" />;
      case 'sms': return <Smartphone className="w-4 h-4" />;
      default: return <Mail className="w-4 h-4" />;
    }
  };

  const getChannelColor = (channel) => {
    switch(channel) {
      case 'email': return 'bg-blue-100 text-blue-800';
      case 'whatsapp': return 'bg-green-100 text-green-800';
      case 'sms': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTriggerIcon = (type) => {
    switch(type) {
      case 'time': return <Clock className="w-3 h-3" />;
      case 'payment': return <DollarSign className="w-3 h-3" />;
      case 'yacht': return <Anchor className="w-3 h-3" />;
      case 'crew': return <Users className="w-3 h-3" />;
      case 'condition': return <Settings className="w-3 h-3" />;
      default: return <Settings className="w-3 h-3" />;
    }
  };

  const getTriggerColor = (type) => {
    switch(type) {
      case 'time': return 'bg-blue-500 text-white';
      case 'payment': return 'bg-red-500 text-white';
      case 'yacht': return 'bg-blue-800 text-white';
      case 'crew': return 'bg-green-500 text-white';
      case 'condition': return 'bg-yellow-600 text-black';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getFlagEmoji = (lang) => {
    const flags = {
      FR: '🇫🇷',
      EN: '🇬🇧', 
      RU: '🇷🇺',
      AR: '🇦🇪',
      ZH: '🇨🇳'
    };
    return flags[lang] || '🏁';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-900 via-blue-800 to-blue-700 text-white p-8 relative overflow-hidden">
        <div className="absolute top-0 right-12 text-8xl opacity-5">
          <Anchor className="w-48 h-48 transform rotate-12" />
        </div>
        <div className="flex items-center justify-between relative z-10">
          <div>
            <h1 className="text-4xl font-light tracking-widest mb-2">COMMUNICATIONS SUPERYACHTS</h1>
            <p className="text-sm tracking-wider opacity-90">Communications multilingues automatisées pour charters selon standards MYBA</p>
            <div className="flex items-center gap-2 mt-4">
              <Badge className="bg-blue-800 text-white">
                <Anchor className="w-3 h-3 mr-1" />
                30m - 100m+
              </Badge>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex bg-blue-800 bg-opacity-50 rounded-full p-1">
              {['FR', 'EN', 'RU', 'AR', 'ZH'].map((lang) => (
                <Button
                  key={lang}
                  variant={activeLanguage === lang ? 'default' : 'ghost'}
                  size="sm"
                  className={`rounded-full text-xs ${activeLanguage === lang ? 'bg-yellow-600 text-black' : 'text-white hover:bg-blue-700'}`}
                  onClick={() => setActiveLanguage(lang)}
                >
                  {lang}
                </Button>
              ))}
            </div>
            <Button variant="secondary" size="sm">
              Tester l'envoi
            </Button>
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white p-6 shadow-sm">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex flex-col gap-1">
            <label className="text-xs uppercase tracking-wide text-gray-500">Canal</label>
            <Select>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Tous les canaux" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les canaux</SelectItem>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="whatsapp">WhatsApp</SelectItem>
                <SelectItem value="sms">SMS Satellite</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs uppercase tracking-wide text-gray-500">Phase Charter</label>
            <Select>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Toutes les phases" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les phases</SelectItem>
                <SelectItem value="enquiry">Enquiry</SelectItem>
                <SelectItem value="holding">Option/Holding</SelectItem>
                <SelectItem value="confirmation">Confirmation</SelectItem>
                <SelectItem value="pre">Pre-embarkation</SelectItem>
                <SelectItem value="during">During charter</SelectItem>
                <SelectItem value="disembark">Disembarkation</SelectItem>
                <SelectItem value="post">Post-charter</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs uppercase tracking-wide text-gray-500">Type Yacht</label>
            <Select>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Tous types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous types</SelectItem>
                <SelectItem value="motor">Motor Yacht</SelectItem>
                <SelectItem value="sailing">Sailing Yacht</SelectItem>
                <SelectItem value="explorer">Explorer</SelectItem>
                <SelectItem value="catamaran">Catamaran</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex-1 min-w-80">
            <label className="text-xs uppercase tracking-wide text-gray-500">Recherche</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input 
                placeholder="Template, phase, déclencheur..." 
                className="pl-10"
              />
            </div>
          </div>

          <Dialog>
            <DialogTrigger asChild>
              <Button className="bg-yellow-600 hover:bg-yellow-700 text-black">
                <Plus className="w-4 h-4 mr-2" />
                Nouveau Template
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl">
              <DialogHeader>
                <DialogTitle>Créer un nouveau template de communication</DialogTitle>
              </DialogHeader>
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Nom du template</label>
                    <Input placeholder="Ex: Charter Confirmation" />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Phase du charter</label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner une phase" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="enquiry">Enquiry</SelectItem>
                        <SelectItem value="confirmation">Confirmation</SelectItem>
                        <SelectItem value="pre">Pre-embarkation</SelectItem>
                        <SelectItem value="during">During charter</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium">Canaux de communication</label>
                  <div className="flex gap-2 mt-2">
                    <Badge variant="outline" className="cursor-pointer hover:bg-blue-50">
                      <Mail className="w-3 h-3 mr-1" />
                      Email
                    </Badge>
                    <Badge variant="outline" className="cursor-pointer hover:bg-green-50">
                      <MessageSquare className="w-3 h-3 mr-1" />
                      WhatsApp
                    </Badge>
                    <Badge variant="outline" className="cursor-pointer hover:bg-orange-50">
                      <Smartphone className="w-3 h-3 mr-1" />
                      SMS
                    </Badge>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium">Variables disponibles</label>
                  <div className="mt-2 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex flex-wrap gap-2">
                      {['{{yacht_name}}', '{{guest_name}}', '{{charter_dates}}', '{{captain_name}}', '{{marina_location}}', '{{apa_amount}}'].map((variable) => (
                        <Badge key={variable} variant="outline" className="cursor-pointer hover:bg-blue-100 text-blue-800 border-blue-300">
                          {variable}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>

                <Button className="w-full bg-yellow-600 hover:bg-yellow-700 text-black">
                  Créer le template
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Content */}
      <div className="p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {communicationTemplates.map((template) => (
            <Card key={template.id} className={`transition-all hover:shadow-lg ${template.active ? 'border-l-4 border-l-blue-500' : 'border-l-4 border-l-gray-300'}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold">{template.name}</h3>
                      <div className="flex gap-2">
                        {template.channels.map((channel) => (
                          <Badge key={channel} className={`${getChannelColor(channel)} flex items-center gap-1`}>
                            {getChannelIcon(channel)}
                            {channel.charAt(0).toUpperCase() + channel.slice(1)}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mb-3">
                      {template.languages.map((lang) => (
                        <span key={lang} className="text-lg">{getFlagEmoji(lang)}</span>
                      ))}
                    </div>
                    <p className="text-sm text-gray-600">{template.description}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center">
                      <div className={`w-3 h-3 rounded-full ${template.active ? 'bg-green-500' : 'bg-gray-400'} mr-2`}></div>
                      <span className="text-xs text-gray-500">{template.active ? 'Actif' : 'Inactif'}</span>
                    </div>
                    <Button variant="ghost" size="sm">
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm">
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm">
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="mb-4">
                  <h4 className="text-xs uppercase tracking-wide text-gray-500 mb-3">Déclencheurs configurés</h4>
                  <div className="flex flex-wrap gap-2">
                    {template.triggers.map((trigger, index) => (
                      <Badge key={index} variant="secondary" className={`${getTriggerColor(trigger.type)} flex items-center gap-1`}>
                        {getTriggerIcon(trigger.type)}
                        {trigger.text}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-6 pt-4 border-t">
                  {Object.entries(template.stats).map(([key, value]) => (
                    <div key={key} className="text-center">
                      <div className="text-xl font-light text-gray-900">{value}</div>
                      <div className="text-xs uppercase tracking-wide text-gray-500">
                        {key === 'sent' && 'Envoyés'}
                        {key === 'signatureRate' && 'Signature rate'}
                        {key === 'avgTime' && 'Temps signature'}
                        {key === 'charterValue' && 'Charter value'}
                        {key === 'completionRate' && 'Completion rate'}
                        {key === 'crewSatisfaction' && 'Crew satisfaction'}
                        {key === 'avgProvisioningCost' && 'Avg provisioning'}
                        {key === 'readRate' && 'Read rate'}
                        {key === 'onTimeEmbark' && 'On time embark'}
                        {key === 'guestSatisfaction' && 'Guest satisfaction'}
                        {key === 'responseRate' && 'Response rate'}
                        {key === 'itineraryChanges' && 'Itinerary changes'}
                        {key === 'avgDailySpend' && 'Avg daily spend'}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Floating Action Button */}
      <Dialog>
        <DialogTrigger asChild>
          <Button 
            className="fixed bottom-8 right-8 w-14 h-14 rounded-full shadow-lg bg-yellow-600 hover:bg-yellow-700 text-black hover:scale-110 transition-transform"
            size="icon"
          >
            <Plus className="w-6 h-6" />
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Actions rapides</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <Button variant="outline" className="justify-start">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau template
            </Button>
            <Button variant="outline" className="justify-start">
              <BarChart3 className="w-4 h-4 mr-2" />
              Analytics détaillées
            </Button>
            <Button variant="outline" className="justify-start">
              <Settings className="w-4 h-4 mr-2" />
              Workflows MYBA
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default YachtCommunications;